// CONTRAT PARTAGÉ du cycle de vie RDV (nouveau modèle de facturation).
// Source de vérité UNIQUE côté front : libellés, couleurs, transitions.
// Toutes les vues (SDR, CSM, client, direction) lisent CE fichier — jamais de
// libellé de statut en dur ailleurs. Miroir de public.advance_appointment (DB).
//
// Les transitions elles-mêmes sont validées EN BASE par advance_appointment().
// Ici on ne fait que refléter l'UI ; on n'écrit jamais lifecycle_status en direct.

export type LifecycleStatus =
  | 'posed' | 'confirmed' | 'held' | 'no_show' | 'rescheduled'
  | 'qualified' | 'not_qualified' | 'validated' | 'disputed' | 'billed' | 'cancelled';

export type Tone = 'neutral' | 'progress' | 'success' | 'warning' | 'danger';

interface StatusMeta {
  label: string;        // libellé FR unique (jamais "Completed"/"showed"/"effectué")
  tone: Tone;
  description: string;
}

// Libellés alignés sur le dictionnaire hook-brain (socle/definitions.md §1) : on ne dit
// jamais « RDV » tout court, on dit booké / confirmé / honoré / qualifié. Note de dette :
// le cerveau veut ces 4 comme dimensions EMPILÉES (4 champs), pas une chaîne sur un seul
// lifecycle_status — migration 4-colonnes = chantier data n°1, cf hook-brain chantiers.
export const LIFECYCLE: Record<LifecycleStatus, StatusMeta> = {
  posed:         { label: 'Booké',         tone: 'neutral',  description: 'Créneau posé avec un prospect (booking_date renseignée)' },
  confirmed:     { label: 'Confirmé',      tone: 'progress', description: 'Prospect joint et confirmé avant le RDV' },
  held:          { label: 'Honoré',        tone: 'progress', description: 'Le RDV a eu lieu (le prospect s’est présenté)' },
  no_show:       { label: 'Non honoré',    tone: 'danger',   description: 'Le rendez-vous n’a pas eu lieu → à reprogrammer' },
  rescheduled:   { label: 'Reporté',       tone: 'warning',  description: 'Reprogrammé → repart au statut Booké' },
  qualified:     { label: 'Qualifié',      tone: 'progress', description: 'Honoré ET conforme au Sales Book (ICP + questions de qualif posées et répondues)' },
  not_qualified: { label: 'Non qualifié',  tone: 'danger',   description: 'Hors critères du calibrage (motif obligatoire)' },
  validated:     { label: 'À facturer',    tone: 'success',  description: 'Encaissable — aucune contestation dans la fenêtre' },
  disputed:      { label: 'Contesté',      tone: 'warning',  description: 'Contestation client → arbitrage fondateur' },
  billed:        { label: 'Facturé',       tone: 'success',  description: 'Rattaché à une période mensuelle verrouillée' },
  cancelled:     { label: 'Annulé',        tone: 'neutral',  description: 'Annulé — réouvrable en reporté/booké (interne)' },
};

// Transitions autorisées (miroir de la table en base). Sert à afficher les BONS
// boutons d’action selon l’état courant et le rôle. La base reste seule juge.
// Miroir EXACT de public.advance_appointment (migration 20260825_advance_appointment_reversible).
// Réversible : chaque avancée (honoré/qualifié/non qualifié) a son retour arrière d'un cran.
// Tout le monde (client compris) peut aller jusqu'à qualifié/non-qualifié et revenir ;
// création/confirmation/report et facturation (validated/billed/arbitrage) restent internes.
export const TRANSITIONS: Array<{ from: LifecycleStatus | null; to: LifecycleStatus; roles: string[] }> = [
  { from: null,          to: 'posed',         roles: ['sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'posed',       to: 'confirmed',     roles: ['sdr', 'csm', 'admin', 'super_admin', 'system'] },
  { from: 'posed',       to: 'held',          roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'posed',       to: 'no_show',       roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'posed',       to: 'rescheduled',   roles: ['sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'confirmed',   to: 'held',          roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'confirmed',   to: 'no_show',       roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'confirmed',   to: 'rescheduled',   roles: ['sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'confirmed',   to: 'posed',         roles: ['sdr', 'csm', 'admin', 'super_admin'] },            // retour arrière
  { from: 'held',        to: 'qualified',     roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'held',        to: 'not_qualified', roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'held',        to: 'posed',         roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },  // retour arrière
  { from: 'held',        to: 'disputed',      roles: ['client', 'csm', 'admin', 'super_admin'] },
  { from: 'qualified',   to: 'held',          roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },  // retour arrière
  { from: 'qualified',   to: 'validated',     roles: ['csm', 'admin', 'super_admin', 'system'] },
  { from: 'not_qualified', to: 'held',        roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },  // retour arrière
  { from: 'validated',   to: 'disputed',      roles: ['client', 'csm', 'admin', 'super_admin'] },
  { from: 'validated',   to: 'billed',        roles: ['admin', 'super_admin', 'system'] },
  { from: 'disputed',    to: 'validated',     roles: ['admin', 'super_admin'] },
  { from: 'disputed',    to: 'cancelled',     roles: ['admin', 'super_admin'] },
  { from: 'rescheduled', to: 'posed',         roles: ['sdr', 'csm', 'admin', 'super_admin'] },
  // Un RDV reporté se statue directement (comme un booké) : sinon il faut « revenir à booké »
  // avant de pouvoir confirmer/honorer/no-show → bloquait les confirmations (retours équipe 25/09).
  { from: 'rescheduled', to: 'confirmed',     roles: ['sdr', 'csm', 'admin', 'super_admin', 'system'] },
  { from: 'rescheduled', to: 'held',          roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'rescheduled', to: 'no_show',       roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'rescheduled', to: 'rescheduled',   roles: ['sdr', 'csm', 'admin', 'super_admin'] },           // re-reporter
  { from: 'no_show',     to: 'posed',         roles: ['client', 'sdr', 'csm', 'admin', 'super_admin'] },  // retour arrière
  // Réouverture d'un RDV annulé (fix 14/10) — interne uniquement. « annulé » n'est plus un cul-de-sac :
  // un prospect qui annule puis reprend peut être remis en reporté/booké (miroir de advance_appointment).
  { from: 'cancelled',   to: 'posed',         roles: ['sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'cancelled',   to: 'rescheduled',   roles: ['sdr', 'csm', 'admin', 'super_admin'] },
  { from: 'cancelled',   to: 'confirmed',     roles: ['sdr', 'csm', 'admin', 'super_admin'] },
];

// Type de contrat côté facturation. 'variable' = facture au RDV (fixed_plus_variable
// en base) ; tout le reste (fixe, jours) ne facture pas au RDV.
export type ContractType = 'variable' | 'fixed' | 'days' | 'gtm';

// Vocabulaire figé avec l'équipe (2026-07-22, langage fondateur) : les clés internes
// (base + code) restent stables ; SEULS ces libellés FR s'affichent. Un seul endroit.
//   forfait = contrat fixe (pas de facturation au RDV)
//   hybride = fixe + variable (facturation au RDV qualifié)
export const CONTRACT_LABELS: Record<ContractType, string> = {
  fixed:    'Forfait',
  variable: 'Hybride',
  days:     'Jours de prospection',
  gtm:      'GTM',
};

// États réservés aux contrats qui facturent au RDV (partie facturation).
export const BILLING_STATES: LifecycleStatus[] = ['validated', 'billed'];

/** Un contrat facture-t-il au RDV ? (seul le variable ouvre Validé/Facturé) */
export const billsPerRdv = (c?: ContractType | null) => c === 'variable';

/**
 * Actions possibles depuis l'état courant, pour un rôle ET un type de contrat donnés.
 * Qualification universelle ; Validé/Facturé seulement si le contrat facture au RDV.
 * contractType omis (null) = permissif (le moteur en base reste le juge final).
 */
export function allowedTransitions(
  from: LifecycleStatus | null,
  role: string,
  contractType?: ContractType | null
): LifecycleStatus[] {
  const base = TRANSITIONS
    .filter(t => t.from === from && t.roles.includes(role))
    .map(t => t.to)
    .filter(to => billsPerRdv(contractType) || contractType == null || !BILLING_STATES.includes(to));
  // Lancement client V2 (réunion 15/10, Kevin : « dangereux de laisser modifiable par tout le monde ») :
  // le client est en LECTURE SEULE sur le cycle de vie — l'équipe statue (vue « RDV à statuer »).
  // Seule exception : la contestation (fenêtre de litige). Garde-fou UI ; l'enforcement DB est un P1.
  if (role === 'client') return base.filter(to => to === 'disputed');
  return base;
}

export const isBillable = (s: LifecycleStatus | null) => s === 'validated' || s === 'billed';
